import {ViewportConfig} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/ViewportConfig';
import {ViewportModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/ViewportModel';
import {ViewportUtil} from '@mintdata-wsp/common/util/ViewportUtil';
import {Component} from '@mintdata/mint-kit/api/decorators/Component';
import {Event} from '@mintdata/mint-kit/api/decorators/Event';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {AnEvent} from '@mintdata/mint-kit/api/events/EventFn';
import {AFocusEvent, AKeyboardEvent, AMouseEvent, APageAppearingEvent} from '@mintdata/mint-kit/api/events/EventType';
import {BoxHostId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Overflow} from '@mintdata/mint-kit/api/models/Overflow';
import {Size} from '@mintdata/mint-kit/api/models/Size';
import {BaseProps} from '@mintdata/mint-kit/api/properties/BaseProps';
import bind from 'bind-decorator';
import * as React from 'react';

export interface IViewportInfo
{
    isInOverlays:boolean;
    isHiddenOverlay:boolean;
    pageId:string;
    isTopmost:boolean;
    isActive:boolean;
    onResize:(width:number, height:number, type:string) => void;
    closeOverlayFunction:() => boolean;
    surfaceAreaRef:HTMLElement | null;
    isInDesignMode:boolean;
}

export class ViewportProps extends BaseProps
{
    @Property<ViewportModel>({
        title: '',
        tip: '',
        isRequired: false,
        typeConfig: ViewportConfig,
        typeCtor: ViewportModel
    })
    model?:ViewportModel = ViewportModel.default();

    viewportProps?:IViewportInfo;

    @Event({title: 'onPageShown', tip: 'Page shown'})
    onPageShown?:AnEvent<APageAppearingEvent>;

    @Event({title: 'onPageHidden', tip: 'Page hidden'})
    onPageHidden?:AnEvent<APageAppearingEvent>;

    @Event({isEnabled: true, title: 'onClick', tip: 'Click'})
    onClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onDoubleClick', tip: 'Double click'})
    onDoubleClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseDown', tip: 'Mouse down'})
    onMouseDown?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseUp', tip: 'Mouse up'})
    onMouseUp?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseMove', tip: 'Mouse move'})
    onMouseMove?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOver', tip: 'Mouse over'})
    onMouseOver?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOut', tip: 'Mouse out'})
    onMouseOut?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseEnter', tip: 'Mouse enter'})
    onMouseEnter?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseLeave', tip: 'Mouse leave'})
    onMouseLeave?:AnEvent<AMouseEvent>;

    @Event({title: 'onFocused', tip: 'Focused'})
    onFocused?:AnEvent<AFocusEvent>;

    @Event({title: 'onLostFocus', tip: 'Lost focus'})
    onLostFocus?:AnEvent<AFocusEvent>;

    @Event({title: 'onKeyDown', tip: 'Key down'})
    onKeyDown?:AnEvent<AKeyboardEvent>;

    @Event({title: 'onKeyPress', tip: 'Key press'})
    onKeyPress?:AnEvent<AKeyboardEvent>;

    @Event({title: 'onKeyUp', tip: 'Key up'})
    onKeyUp?:AnEvent<AKeyboardEvent>;
}

@Component<ViewportProps>(
    {
        name: 'Viewport',
        tip: 'Viewport',
        props: ViewportProps,
        supportsChildren: true,
        isSvgCompatible: false,
        requireSize: true,
        width: 768,
        height: 1024
    })
export class Viewport extends React.Component<ViewportProps, any>
{
    public constructor(props:ViewportProps)
    {
        super(props);
    }

    private _ref:HTMLElement | null = null;

    @bind
    private _refHandler(ref:HTMLElement | null)
    {
        this._ref = ref;
    };

    componentDidMount()
    {
        if( this.props.viewportProps && this.props.viewportProps.isInOverlays )
        {
            const {model: {closeByClick, passEventsThrough}, viewportProps} = this.props;

            if( viewportProps.isTopmost && this._ref )
                (this._ref as any).focus({preventScroll: true});

            if( closeByClick.value && passEventsThrough.value )
                this._addOutsideClickHandler();
        }
    }

    componentDidUpdate(prevProps:ViewportProps)
    {
        if( this.props.viewportProps )
        {
            const curViewportProps:IViewportInfo = this.props.viewportProps;
            const prevViewportProps:IViewportInfo = prevProps.viewportProps;

            const curIsTopmost:boolean = curViewportProps.isTopmost;
            const prevIsTopmost:boolean = prevViewportProps.isTopmost;
            const curIsInOverlays:boolean = curViewportProps.isInOverlays;
            const prevIsInOverlays:boolean = prevViewportProps.isInOverlays;

            const curCloseByClick:boolean = this.props.model.closeByClick.value;
            const prevCloseByClick:boolean = prevProps.model.closeByClick.value;
            const curPassEventsThrough:boolean = this.props.model.passEventsThrough.value;
            const prevPassEventsThrough:boolean = prevProps.model.passEventsThrough.value;

            if( curViewportProps.isActive )
            {
                const currentSize:Size = this.props.size;
                const prevSize:Size = prevProps.size;

                if( currentSize.width.value !== prevSize.width.value || currentSize.height.value !== prevSize.height.value )
                {
                    curViewportProps.onResize(
                        currentSize.width.value,
                        currentSize.height.value,
                        ViewportUtil.getViewportTypeByWidth(currentSize.width.value)
                    );
                }
            }

            if( curIsInOverlays && !prevIsInOverlays )
            {
                if( curIsTopmost && !prevIsTopmost && this._ref )
                    (this._ref as any).focus({preventScroll: true});

                if( curCloseByClick && curPassEventsThrough )
                    this._addOutsideClickHandler();
            }

            if( !curIsInOverlays && prevIsInOverlays )
            {
                if( curCloseByClick && curPassEventsThrough )
                    this._removeOutsideClickHandler();
            }

            if( curIsInOverlays )
            {
                if( curCloseByClick && curPassEventsThrough && (!prevCloseByClick || !prevPassEventsThrough) )
                    this._addOutsideClickHandler();
                else if( prevCloseByClick && prevPassEventsThrough && (!curCloseByClick || !curPassEventsThrough) )
                    this._removeOutsideClickHandler();
            }
        }
    }

    componentWillUnmount()
    {
        if( this.props.viewportProps && this.props.viewportProps.isInOverlays )
            this._removeOutsideClickHandler();
    }

    @bind
    private _addOutsideClickHandler():void
    {
        this.props.viewportProps.surfaceAreaRef.addEventListener('click', this._handleClickOutside);
    }

    @bind
    private _removeOutsideClickHandler():void
    {
        this.props.viewportProps.surfaceAreaRef.removeEventListener('click', this._handleClickOutside);
    }

    @bind
    private _handleClickOutside(event:MouseEvent):void
    {
        const {viewportProps: {surfaceAreaRef, closeOverlayFunction, isInDesignMode}} = this.props;

        if( isInDesignMode || this._isItChildOfViewport(event.target as HTMLElement, this._ref, surfaceAreaRef) )
            return;

        closeOverlayFunction();
    }

    private _isItChildOfViewport(current:HTMLElement, ref:HTMLElement, highestElToMatch:HTMLElement):boolean
    {
        while( current.parentElement || current === highestElToMatch )
        {
            if( current.getAttribute('data-box-title') || current === ref )
                return true;

            current = current.parentElement;
        }

        return false;
    }

    @bind
    private _onClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onClick, model: {closeByClick}, viewportProps} = this.props;

        if( event.currentTarget === event.target
            && closeByClick && closeByClick.value
            && viewportProps && viewportProps.isInOverlays && viewportProps.closeOverlayFunction )
        {
            viewportProps.closeOverlayFunction();
        }

        if( onClick )
            onClick(new AMouseEvent(event));
    }

    @bind
    private _onDoubleClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onDoubleClick} = this.props;

        if( onDoubleClick )
            onDoubleClick(new AMouseEvent(event));
    }

    @bind
    private _onMouseDown(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseDown} = this.props;

        if( onMouseDown )
            onMouseDown(new AMouseEvent(event));
    }

    @bind
    private _onMouseUp(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseUp} = this.props;

        if( onMouseUp )
            onMouseUp(new AMouseEvent(event));
    }

    @bind
    private _onMouseMove(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseMove} = this.props;

        if( onMouseMove )
            onMouseMove(new AMouseEvent(event));
    }

    @bind
    private _onMouseOver(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOver, reparenting} = this.props;

        if( reparenting )
            reparenting.onMouseOverHost(event, null);

        if( onMouseOver )
            onMouseOver(new AMouseEvent(event));
    }

    @bind
    private _onMouseOut(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOut} = this.props;

        if( onMouseOut )
            onMouseOut(new AMouseEvent(event));
    }

    @bind
    private _onMouseEnter(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseEnter} = this.props;

        if( onMouseEnter )
            onMouseEnter(new AMouseEvent(event));
    }

    @bind
    private _onMouseLeave(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseLeave, reparenting} = this.props;

        if( reparenting )
            reparenting.onMouseLeaveHost(event);

        if( onMouseLeave )
            onMouseLeave(new AMouseEvent(event));
    }

    @bind
    private _onFocused(event:React.FocusEvent<HTMLElement>):void
    {
        const {onFocused} = this.props;

        if( onFocused )
            onFocused(new AFocusEvent(event));
    }

    @bind
    private _onLostFocus(event:React.FocusEvent<HTMLElement>):void
    {
        const {onLostFocus} = this.props;

        if( onLostFocus )
            onLostFocus(new AFocusEvent(event));
    }

    @bind
    private _onKeyPress(event:React.KeyboardEvent<HTMLElement>):void
    {
        const {onKeyPress} = this.props;

        if( onKeyPress )
            onKeyPress(new AKeyboardEvent(event));
    }

    @bind
    private _onKeyUp(event:React.KeyboardEvent<HTMLElement>):void
    {
        const {onKeyUp} = this.props;

        if( onKeyUp )
            onKeyUp(new AKeyboardEvent(event));
    }

    @bind
    private _onKeyDown(event:React.KeyboardEvent<HTMLDivElement>):void
    {
        const {model: {closeOnEsc}, viewportProps, onKeyDown} = this.props;

        if( event.key.toLowerCase() === 'escape' && closeOnEsc && closeOnEsc.value
            && viewportProps && viewportProps.isInOverlays && viewportProps.closeOverlayFunction )
        {
            event.stopPropagation();
            viewportProps.closeOverlayFunction();
        }

        if( onKeyDown )
            onKeyDown(new AKeyboardEvent(event));
    }

    public render():JSX.Element
    {
        const {
            children,
            model: {
                bg,
                overflowX = Overflow.hidden(),
                overflowY = Overflow.auto(),
                passEventsThrough,
                accelerateScrollOnMobile
            },
            viewportProps,
            reparenting
        } = this.props;

        const bgCssString:string = bg ? bg.toCssString() : null;

        let style:React.CSSProperties = {
            height: '100%',
            backgroundColor: bgCssString,
            overflowX: Overflow.cssValue(overflowX),
            overflowY: Overflow.cssValue(overflowY),
            pointerEvents: viewportProps && viewportProps.isInOverlays
            && (viewportProps.isHiddenOverlay || (passEventsThrough && passEventsThrough.value))
                ? 'none'
                : 'auto'
        };

        if( accelerateScrollOnMobile.value )
            style = {...style, WebkitOverflowScrolling: 'touch'};

        const hostId:BoxHostId = null;
        const mouseOverDroppableHost:BoxHostId = reparenting && reparenting.mouseOverDroppableHost;
        const isDropIntoBoxAllowed:boolean = reparenting && reparenting.isDropIntoBoxAllowed;
        const isMouseOverDroppableHost:boolean = mouseOverDroppableHost === hostId;

        return (
            <div
                data-drop-into-box-allowed={isDropIntoBoxAllowed}
                data-is-mouse-over-droppable-host={isMouseOverDroppableHost}
                style={style}
                tabIndex={-1}
                ref={this._refHandler}
                onClick={this._onClick}
                onDoubleClick={this._onDoubleClick}
                onMouseDown={this._onMouseDown}
                onMouseUp={this._onMouseUp}
                onMouseMove={this._onMouseMove}
                onMouseOver={this._onMouseOver}
                onMouseOut={this._onMouseOut}
                onMouseEnter={this._onMouseEnter}
                onMouseLeave={this._onMouseLeave}
                onFocus={this._onFocused}
                onBlur={this._onLostFocus}
                onKeyDown={this._onKeyDown}
                onKeyPress={this._onKeyPress}
                onKeyUp={this._onKeyUp}
            >
                {children}
            </div>
        );
    }
}
